import { COLUMN_ALTERNATE_KEY_CHAR_WIDTH, VIEW_TABLE_HEADER_FONT_SCALE, VIEW_TABLE_HEADER_WEIGHT_SCALE } from "../constants/layout.js";
import { query } from "../erd-editor-schema/dist/query/index.js";
import { Show } from "../constants/schema.js";
import { textInRange } from "./validation.js";
import { observable } from "../r-html/dist/observable/index.js";
import { getLiveDomainName } from "../engine/modules/dictionary/membership.js";
import { bHas } from "./bit.js";
import { FocusType } from "../engine/modules/editor/state.js";
import { drawsDomainInTypeCell, drawsNameForLogicalName, getTableFocusTypes, isBothNameView, isLogicalNameView, sceneNameSettings } from "../engine/modules/editor/utils/nameView.js";
import { getAlternateKeyMarks } from "./tableKeys.js";
//#region src/utils/calcTable.ts
var widthGeneration = 0;
var widthSignal = observable({ generation: 0 });
function getWidthGeneration() {
	return widthGeneration;
}
function trackTableWidths() {
	return widthSignal.generation;
}
function invalidateTableWidths() {
	widthGeneration++;
	widthSignal.generation = widthGeneration;
}
function calcAlternateKeyWidth(table, state) {
	if (!bHas(state.settings.show, Show.columnAlternateKey)) return 0;
	return Object.values(getAlternateKeyMarks(state, table)).reduce((width, mark) => Math.max(width, Math.ceil(mark.length * COLUMN_ALTERNATE_KEY_CHAR_WIDTH)), 0);
}
function headerCellWidth(state, table, focusType) {
	const { settings } = state;
	switch (focusType) {
		case FocusType.tableComment: return settings.maxWidthComment === -1 || settings.maxWidthComment >= table.ui.widthComment ? table.ui.widthComment : settings.maxWidthComment;
		case FocusType.tableLogicalName: return drawsNameForLogicalName(settings, table) ? table.ui.widthName : table.ui.widthLogicalName;
		default: return table.ui.widthName;
	}
}
function calcTableWidths(table, state) {
	const { settings, collections } = state;
	let width = getTableFocusTypes(settings).reduce((acc, focusType) => acc + headerCellWidth(state, table, focusType) + 8, 20);
	width += 28;
	const alternateKey = calcAlternateKeyWidth(table, state);
	const defaultWidthColumns = calcDefaultWidthColumns(settings) + (alternateKey ? alternateKey + 8 : 0);
	if (width < defaultWidthColumns) width = defaultWidthColumns;
	const maxWidthColumn = calcMaxWidthColumn(query(collections).collection("tableColumnEntities").selectByIds(table.columnIds), state, alternateKey);
	if (width < maxWidthColumn.width) width = maxWidthColumn.width;
	return {
		...maxWidthColumn,
		width: 9 + width + 8 + 1
	};
}
var DEFAULT_WIDTH_COLUMNS = [
	{
		key: Show.columnComment,
		width: 60
	},
	{
		key: Show.columnDataType,
		width: 60
	},
	{
		key: Show.columnDomain,
		width: 60
	},
	{
		key: Show.columnDefault,
		width: 60
	},
	{
		key: Show.columnNotNull,
		width: 35
	},
	{
		key: Show.columnAutoIncrement,
		width: 15
	},
	{
		key: Show.columnUnique,
		width: 22
	}
];
function calcDefaultWidthColumns(settings) {
	const shown = DEFAULT_WIDTH_COLUMNS.reduce((acc, { key, width }) => bHas(settings.show, key) ? acc + width + 8 : acc, 100);
	return isBothNameView(settings) ? shown + 60 + 8 : shown;
}
function createColumnWidth() {
	return {
		width: 0,
		alternateKey: 0,
		name: 0,
		logicalName: 0,
		comment: 0,
		dataType: 0,
		domain: 0,
		default: 0,
		notNull: 0,
		autoIncrement: 0,
		unique: 0
	};
}
function calcMaxWidthColumn(columns, state, alternateKey) {
	const { settings } = state;
	const { show, maxWidthComment } = settings;
	const logical = isLogicalNameView(settings);
	const both = isBothNameView(settings);
	const columnWidth = createColumnWidth();
	columnWidth.alternateKey = alternateKey;
	for (const column of columns) {
		const widthName = logical && !drawsNameForLogicalName(settings, column) ? column.ui.widthLogicalName : column.ui.widthName;
		if (columnWidth.name < widthName) columnWidth.name = widthName;
		if (both && columnWidth.logicalName < column.ui.widthLogicalName) columnWidth.logicalName = column.ui.widthLogicalName;
		if (bHas(show, Show.columnComment) && columnWidth.comment < column.ui.widthComment) columnWidth.comment = maxWidthComment === -1 ? column.ui.widthComment : maxWidthComment < column.ui.widthComment ? maxWidthComment : column.ui.widthComment;
		if (bHas(show, Show.columnDataType)) {
			const widthDataType = drawsDomainInTypeCell(state, column) ? column.ui.widthDomain : column.ui.widthDataType;
			if (columnWidth.dataType < widthDataType) columnWidth.dataType = widthDataType;
		}
		if (bHas(show, Show.columnDomain) && columnWidth.domain < column.ui.widthDomain) columnWidth.domain = column.ui.widthDomain;
		if (bHas(show, Show.columnDefault) && columnWidth.default < column.ui.widthDefault) columnWidth.default = column.ui.widthDefault;
	}
	if (bHas(show, Show.columnNotNull)) columnWidth.notNull = 35;
	if (bHas(show, Show.columnAutoIncrement)) columnWidth.autoIncrement = 15;
	if (bHas(show, Show.columnUnique)) columnWidth.unique = 22;
	columnWidth.width = Object.entries(columnWidth).reduce((acc, [key, width]) => {
		if (key === "width" || width === 0) return acc;
		return acc + width + 8;
	}, 32);
	return columnWidth;
}
function calcViewTableWidths(table, state, columnIds) {
	const { settings } = state;
	const logical = isLogicalNameView(settings);
	const both = isBothNameView(settings);
	const columnWidth = createColumnWidth();
	const columns = query(state.collections).collection("tableColumnEntities").selectByIds(columnIds);
	for (const column of columns) {
		columnWidth.name = Math.max(columnWidth.name, logical && !drawsNameForLogicalName(settings, column) ? column.ui.widthLogicalName : column.ui.widthName);
		columnWidth.dataType = Math.max(columnWidth.dataType, drawsDomainInTypeCell(state, column, "flow") ? column.ui.widthDomain : column.ui.widthDataType);
		if (both) {
			columnWidth.logicalName = Math.max(columnWidth.logicalName, column.ui.widthLogicalName);
			columnWidth.domain = Math.max(columnWidth.domain, column.ui.widthDomain);
		}
	}
	const cells = both ? [
		columnWidth.logicalName,
		columnWidth.name,
		columnWidth.domain,
		columnWidth.dataType
	] : [columnWidth.name, columnWidth.dataType];
	const rowWidth = columns.length ? cells.reduce((acc, width) => acc + 8 + width, 14) : 0;
	const chrome = 18;
	const content = Math.max(viewHeaderWidth(table, state), rowWidth, 154);
	if (columns.length) columnWidth.name += content - rowWidth;
	columnWidth.width = chrome + content;
	return columnWidth;
}
function viewHeaderNameWidth(widthName) {
	return Math.ceil(widthName * VIEW_TABLE_HEADER_FONT_SCALE * VIEW_TABLE_HEADER_WEIGHT_SCALE);
}
function viewHeaderWidth(table, state) {
	return getTableFocusTypes(sceneNameSettings(state.settings, "flow")).reduce((acc, focusType) => acc + viewHeaderNameWidth(headerCellWidth(state, table, focusType)) + 8, 20) + 28;
}
function tableHeaderHeight(source = "document") {
	return source === "document" ? 20 : 24;
}
function tableRowHeight(source = "document") {
	return source === "document" ? 24 : 32;
}
function calcTableHeight(table, rowCount = table.columnIds.length, source = "document") {
	return 9 + tableHeaderHeight(source) + rowCount * tableRowHeight(source) + 1;
}
var tableTextWidths = (table, toWidth) => ({
	widthName: textInRange(toWidth(table.name)),
	widthLogicalName: textInRange(toWidth(table.logicalName)),
	widthComment: textInRange(toWidth(table.comment))
});
var columnTextWidths = (state, column, toWidth) => ({
	widthName: textInRange(toWidth(column.name)),
	widthLogicalName: textInRange(toWidth(column.logicalName)),
	widthDataType: textInRange(toWidth(column.dataType)),
	widthDomain: textInRange(toWidth(getLiveDomainName(state, column.domainId))),
	widthDefault: textInRange(toWidth(column.default)),
	widthComment: textInRange(toWidth(column.comment))
});
function recalculateTableWidth(state, { toWidth }) {
	const { doc: { tableIds }, collections } = state;
	invalidateTableWidths();
	const tables = query(collections).collection("tableEntities").selectByIds(tableIds);
	const columnCollection = query(collections).collection("tableColumnEntities");
	for (const table of tables) {
		Object.assign(table.ui, tableTextWidths(table, toWidth));
		const columns = columnCollection.selectByIds(table.columnIds);
		for (const column of columns) Object.assign(column.ui, columnTextWidths(state, column, toWidth));
	}
}
function measureTableSize(table, state, toWidth) {
	const columns = query(state.collections).collection("tableColumnEntities").selectByIds(table.columnIds).map((column) => ({
		...column,
		ui: {
			...column.ui,
			...columnTextWidths(state, column, toWidth)
		}
	}));
	const measured = {
		...table,
		ui: {
			...table.ui,
			...tableTextWidths(table, toWidth)
		}
	};
	return {
		width: calcTableWidths(measured, {
			...state,
			collections: {
				...state.collections,
				tableColumnEntities: Object.fromEntries(columns.map((column) => [column.id, column]))
			}
		}).width,
		height: calcTableHeight(measured)
	};
}
//#endregion
export { calcAlternateKeyWidth, calcTableHeight, calcTableWidths, calcViewTableWidths, getWidthGeneration, headerCellWidth, invalidateTableWidths, measureTableSize, recalculateTableWidth, tableHeaderHeight, tableRowHeight, trackTableWidths, viewHeaderNameWidth };
